import * as React from 'react';

let fallbackIdCounter = 0;

/**
 * Neon web components use their `id` in querySelector/getElementById contexts,
 * which reject React's `useId()` output (it contains ':' — invalid in CSS
 * selectors). This returns a stable, colon-free DOM id safe for Neon components.
 *
 * NOTE: `useId` is a React 18 API. When this app runs federated inside a host
 * that pins a shared React 17 singleton (e.g. naviance-admin-tools-v2), it's
 * unavailable. We feature-detect it here (rather than statically
 * `import { useId } from 'react'`) because monkeypatching `useId` onto the
 * shared React module doesn't reliably work under Module Federation — bundlers
 * commonly destructure named imports from the shared module once at load time,
 * so a later patch doesn't affect already-captured references. The
 * `React.useId` presence check is stable for the lifetime of the app (it only
 * depends on which React runtime got loaded, not on props/state), so the
 * hook-call branch below doesn't vary across renders for a given session.
 */
export function useDomId(prefix = 'id'): string {
  const reactUseId = (React as typeof React & { useId?: () => string }).useId;

  if (typeof reactUseId === 'function') {
    const raw = reactUseId();
    return `${prefix}-${raw.replace(/:/g, '')}`;
  }

  // React 17 fallback: no useId, so mint a stable id once per component
  // instance via a ref.
  const idRef = React.useRef<string>();
  if (idRef.current === undefined) {
    idRef.current = `${prefix}-r17-${(fallbackIdCounter++).toString(36)}`;
  }
  return idRef.current;
}
